// src/components/ProjectGrid.jsx
import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import ProjectModal from "./ProjectModal";
import ProjectCard from "./ProjectCard";
import { getProjectsUrl } from "../services/api";
import { getFallbackProjects } from "../data/fallbackProjects";

// Animation variants for grid and cards
const gridParent = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const cardVariant = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.7,
      type: "spring",
      bounce: 0.3,
    },
  },
};



export default function ProjectGrid({ category }) {
  const [projects, setProjects] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [allProjects, setAllProjects] = useState([]);

  const openModal = (index) => {
    setSelectedIndex(index);
    setModalOpen(true);
  };

  // Fetch paginated projects for grid
  const loadProjects = useCallback(
    async (pageNum = 1) => {
      try {
        setLoading(true);
        setError("");
        const url = getProjectsUrl(category, pageNum);
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error(`Server returned HTTP ${res.status}`);
        }
        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch {
          throw new Error("Invalid API response format (HTML error output)");
        }
        if (!data.success) {
          throw new Error(data.error || "API error");
        }
        const items = data.data || [];
        if (pageNum === 1) {
          setProjects(items);
        } else {
          setProjects((prev) => [...prev, ...items]);
        }
        setHasMore(items.length === 9); // 9 is the backend page size
      } catch (e) {
        // Fallback to local curated project data if API connection fails
        const fallbackItems = getFallbackProjects(category, pageNum, 9);
        if (pageNum === 1) {
          setProjects(fallbackItems);
        } else {
          setProjects((prev) => [...prev, ...fallbackItems]);
        }
        setHasMore(fallbackItems.length === 9);
      } finally {
        setLoading(false);
      }
    },
    [category]
  );

  // Fetch all projects for modal
  const loadAllProjects = useCallback(
    async () => {
      try {
        const url = getProjectsUrl(category, 1) + "&all=1";
        const res = await fetch(url);
        if (!res.ok) throw new Error("HTTP error");
        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch {
          throw new Error("JSON parse error");
        }
        if (data && data.success && Array.isArray(data.data) && data.data.length > 0) {
          setAllProjects(data.data);
          return;
        }
        throw new Error("No data");
      } catch {
        setAllProjects(getFallbackProjects(category, 1, 999, true));
      }
    },
    [category]
  );

  useEffect(() => {
    setPage(1);
    setProjects([]);
    setHasMore(true);
    loadProjects(1);
    loadAllProjects();
  }, [category, loadProjects, loadAllProjects]);

  return (
    <>
      <div className="max-w-6xl mx-auto px-4">
        {error && (
          <p className="text-center text-red-500 mt-8 text-sm">{error}</p>
        )}

        {!loading && projects.length === 0 && !error && (
          <p className="text-center text-slate-500 mt-8">
            No projects found for this category.
          </p>
        )}

        <motion.div
          className="grid gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
          variants={gridParent}
          initial="hidden"
          whileInView="show"
          viewport={{ once: false, amount: 0.2 }}
        >
          {projects.map((project, index) => (
            <ProjectCard
              key={project.id}
              project={project}
              onClick={() => openModal(index)}
            />
          ))}
        </motion.div>
        {!loading && hasMore && projects.length > 0 && (
          <div className="flex justify-center mt-8">
            <button
              className="px-6 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition-colors duration-200"
              onClick={() => {
                const next = page + 1;
                setPage(next);
                loadProjects(next);
              }}
              disabled={loading}
            >
              Load More
            </button>
          </div>
        )}

        {loading && (
          <div className="text-center py-8">
            <div className="inline-block w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        )}

      </div>

      {/* Modal */}
      <ProjectModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        projects={allProjects}
        index={selectedIndex}
        setIndex={setSelectedIndex}
      />
    </>
  );
}
